import { DateTime } from 'luxon';
import { FormikValues } from 'formik';

import {
  AdminAllowanceNames,
  AdminLegendVariant,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  expressBooking,
  IAdminBookedPharmacistDetails,
  IAdminWorkshiftDetails,
  TravelAllowanceEnums,
} from '@pharmaplan/common';

import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import { handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';
import { toJSDate } from '../../../components/Dashboard/MainCalendar/helpers';
import {
  decimalConverterProxy,
  handlerForZeroes,
  parseFloatProxy,
} from '../../../helpers/proxyHandlers';

import useExpressBookingTypes from './useExpressBookingTypes';
import { appendTimeToDate } from '../../../helpers/workshift/functions';
import { MomentTimeFormats } from '../../../helpers/Constants';
import { useAppDispatch } from '../../useAppDispatch';

const { posted } = AdminLegendVariant;
const {
  emergencyFees,
  mealAllowance,
  pharmacyHourlyRate,
  accommodationAllowance,
  pharmacistHourlyRate,
} = AdminAllowanceNames;

const {
  travelAllowanceAmount,
  travelAllowanceRatePerKm,
  accommodationAllowanceType: accommodationAllowanceTypeEnum,
  travelAllowanceType,
  otherAllowance,
} = AllowancesNames;

const { meal, others, emergency } = AllowanceEnums;

const useExpressBookingFormik = (
  type: Exclude<AdminLegendVariant, 'total'>,
  expressValues: Array<string>,
  id?: string,
) => {
  const dispatch = useAppDispatch();

  const { detailsApiMap } = useExpressBookingTypes();
  const { details } = detailsApiMap[type];
  const isPosted = type === posted;

  const getFormikInitialValues = () => {
    if (id) {
      const {
        pharmacyName,
        startDate,
        endDate,
        pharmacyId,
        notes,
        pharmacistHourlyRate: formikPharmacistRate,
        pharmacyHourlyRate: formikPharmacyRate,
        emergencyFees: formikEmergencyFees,
        mealAllowance: formikMealAllowance,
        accommodationAllowance: formikAccommodationAllowance,
        travelAllowanceRatePerKm: formiktravelAllowanceRatePerKm,
        travelAllowanceAmount: formikTravelAllowanceAmount,
        accommodationAllowanceType,
        travelAllowanceTypes,
        otherAllowance: formikOtherAllowance,
      } = (details as unknown as IAdminWorkshiftDetails) ?? {};

      const { firstName, lastName } = (details as IAdminBookedPharmacistDetails) ?? {};
      const clientId = details[idStore.id as keyof typeof details];
      const name = firstName ? `${firstName} ${lastName}` : '';

      const allowances = {
        [emergencyFees]: formikEmergencyFees,
        [mealAllowance]: formikMealAllowance,
        [travelAllowanceAmount]: formikTravelAllowanceAmount,
        [travelAllowanceRatePerKm]: formiktravelAllowanceRatePerKm,
        [accommodationAllowance]: formikAccommodationAllowance,
        [pharmacistHourlyRate]: formikPharmacistRate,
        [pharmacyHourlyRate]: formikPharmacyRate,
        [otherAllowance]: formikOtherAllowance,
      };

      const handledForZeros = new Proxy(allowances, handlerForZeroes);
      const handledForDecimals = new Proxy(
        handledForZeros,
        decimalConverterProxy,
      );

      return {
        selectedDate: DateTime.fromISO(startDate, { setZone: true }),
        pharmacies: '',
        pharmacists: name,
        pharmacySelected: {
          label: pharmacyName ?? '',
          id: pharmacyId ?? '',
          subtitle: '',
        },
        pharmacistSelected: isPosted
          ? null
          : {
            label: name,
            id: clientId,
            subtitle: '',
          },
        pharmacyId,
        pharmacistId: clientId,
        startTime: toJSDate(startDate),
        endTime: toJSDate(endDate),
        pharmacistHourlyRate,
        notes: notes ?? '',
        [accommodationAllowanceTypeEnum]: accommodationAllowanceType,
        [AllowancesNames.travelAllowanceType]:
          travelAllowanceTypes?.filter?.(Boolean) ?? [],
        ...handledForDecimals,
      };
    }

    return {
      selectedDate: '',
      pharmacies: '',
      pharmacists: '',
      pharmacyId: '',
      pharmacistId: '',
      pharmacistSelected: null,
      pharmacySelected: null,
      startTime: null,
      endTime: null,
      [emergencyFees]: '',
      [travelAllowanceAmount]: '',
      [travelAllowanceRatePerKm]: '',
      [otherAllowance]: null,
      [accommodationAllowance]: '',
      [mealAllowance]: '',
      [pharmacistHourlyRate]: '',
      [pharmacyHourlyRate]: '',
      [accommodationAllowanceTypeEnum]: null,
      [travelAllowanceType]: [],
    };
  };

  const bookWorkshift = (values: FormikValues) => {
    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKm],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmount],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(values[meal], values[mealAllowance], null),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceTypeEnum] === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowance],
        null,
      ),
      emergencyFees: handleWorkshiftToggles(values[emergency], values[emergencyFees], null),
      otherAllowance: handleWorkshiftToggles(values[others], values[otherAllowance], null),
    };

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    const startD = appendTimeToDate(
      values.selectedDate,
      values.startTime,
    ).toFormat(MomentTimeFormats.fullDate);
    const endD = appendTimeToDate(values.selectedDate, values.endTime).toFormat(
      MomentTimeFormats.fullDate,
    );

    const rest = isPosted
      ? {
        workshiftId: id,
      }
      : {
        bookingId: id,
      };

    dispatch(
      expressBooking({
        startDate: startD,
        endDate: endD,
        pharmacyId: values.pharmacyId,
        [idStore.id]: values.pharmacistId,
        notes: values.notes,
        notesForSalesOrder: values.notesForSalesOrder,
        pharmacistHourlyRate: parseFloat(values.pharmacistHourlyRate ?? '0'),
        pharmacyHourlyRate: parseFloat(values.pharmacyHourlyRate ?? '0'),
        sendEmailToPharmacist: values.sendEmailToPharmacist,
        sendEmailToPharmacy: values.sendEmailToPharmacy,
        travelAllowanceTypes: values[travelAllowanceType],
        accommodationAllowanceType: values[accommodationAllowanceTypeEnum] || null,
        ...handledForDecimals,
        ...rest,
      }),
    );
  };

  return { getFormikInitialValues, bookWorkshift };
};

export default useExpressBookingFormik;
